import { createLogger, type CrucibleSessionConnection } from '@sorrem/shared';
import { useSyncExternalStore } from 'react';
// Imported from its concrete module, not the `@sorrem-client/ui/hooks`
// barrel: the barrel re-exports `auth-session.ts` - routing through it here
// would let anything importing this module from `auth-session.ts` form a cycle.
import { GameStateBridge } from '@sorrem-client/ui/shared/game-state-bridge';
import { ClientRunMetrics, getClientDisplayInfo, recordClientMetricEvent, reportClientCrash } from '@sorrem-client/ui/telemetry';
import { registerCleanRenderSource } from '@sorrem-client/ui/snapshot/clean-render-sources';
import type { CleanRenderResult } from '@sorrem-client/types';
import { createGamePageHost, type GameConnectionMessage, type GameConnector, type GamePageHost, type GameSceneMessage, type GameSessionApi } from './game-page-host';
import type { CrucibleGatheringCommand, GameWorkerMessage, GameWorkerRequest } from './protocol';
import { createPendingCalls } from './pending-calls';

export type { GameSessionApi } from './game-page-host';

export interface GameSessionHandle {
    readonly crucibleId: string;
    readonly canvas: HTMLCanvasElement;
    readonly host: GamePageHost;
    readonly bridge: GameStateBridge;
    readonly gameplayApi: GameSessionApi;
}

const CANVAS_ELEMENT_ID = 'sorrem-game-canvas';

/** Where pages mount the game canvas: behind #ui-root, outside React (see game-canvas.scss). */
export const GAME_CANVAS_MOUNT_SELECTOR = '#game-canvas-root';

let session: GameSessionHandle | undefined;
/** Takes the session's scene back out of developer snapshots' clean renders (#303). */
let unregisterCleanRenderSource: () => void = () => undefined;
const listeners = new Set<() => void>();
const sceneMessageListeners = new Set<(message: GameSceneMessage) => void>();

function notify(): void {
    for (const listener of listeners) {
        listener();
    }
}

function createCanvas(): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.id = CANVAS_ELEMENT_ID;
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    return canvas;
}

const logger = createLogger('client:game-session');

/** How long a stopping game gets to disconnect cleanly before its worker is terminated anyway. */
const GAME_WORKER_STOP_TIMEOUT_MS = 2000;

/**
 * Runs the game in its own worker (game-worker-entry.ts), which also owns the
 * scene: the canvas is transferred there and the worker draws straight to it.
 */
const connectGameWorker: GameConnector = (canvas, onMessage) => {
    const worker = new Worker(new URL('./game-worker-entry.ts', import.meta.url), { type: 'module' });
    const post = (request: GameWorkerRequest, transfer: Transferable[] = []): void => {
        worker.postMessage(request, transfer);
    };
    let stopTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let resolveStopped: (() => void) | null = null;
    const pendingCleanRenders = createPendingCalls<CleanRenderResult>({ ok: false, reason: 'The game stopped before the render finished.' });
    const terminate = (): void => {
        if (stopTimeoutId !== null) {
            clearTimeout(stopTimeoutId);
        }
        worker.terminate();
        resolveStopped?.();
        pendingCleanRenders.abandon();
    };

    worker.onmessage = (event: MessageEvent<GameWorkerMessage>): void => {
        const message = event.data;
        if (message.type === 'stopped') {
            terminate();
            return;
        }
        if (message.type === 'clean-render-result') {
            pendingCleanRenders.settle(message.callId, message.result);
            return;
        }
        if (message.type === 'worker-error') {
            reportClientCrash({ kind: 'game-worker', message: message.message, stack: message.stack });
            return;
        }
        onMessage(message);
    };
    worker.onerror = (event: ErrorEvent): void => {
        logger.error('game worker error:', event.message);
        // The game can't be trusted to go on: say so instead of freezing (#72).
        onMessage({ type: 'ui-state', patch: { fatalError: 'The game stopped unexpectedly.' } });
    };

    return {
        begin: (start): void => {
            const offscreen = canvas.transferControlToOffscreen();
            if (start.kind === 'run') {
                post({ type: 'start', canvas: offscreen, options: start.options }, [offscreen]);
            } else {
                post({ type: 'start-gathering', canvas: offscreen, withGathering: start.withGathering }, [offscreen]);
            }
        },
        startRun: (options) => post({ type: 'start-run', options }),
        sendGathering: (command) => post({ type: 'gathering-command', command }),
        send: (command) => post(command),
        captureCleanRender: (scale) => pendingCleanRenders.call((callId) => post({ type: 'capture-clean-render', callId, scale })),
        dispose: (): Promise<void> => {
            // Let the game disconnect its socket itself, so the host sees a
            // deliberate leave rather than a dropped connection.
            worker.onmessage = (event: MessageEvent<GameWorkerMessage>): void => {
                if (event.data.type === 'stopped') {
                    terminate();
                }
            };
            const stopped = new Promise<void>((resolve) => {
                resolveStopped = resolve;
            });
            stopTimeoutId = setTimeout(terminate, GAME_WORKER_STOP_TIMEOUT_MS);
            post({ type: 'stop' });
            return stopped;
        },
    };
};

/**
 * Wraps a connector so the game's messages also feed telemetry: failures it
 * shows the player (#212), and the run's result and frame timing (#213).
 */
function withGameMetrics(connect: GameConnector, runMetrics: ClientRunMetrics): GameConnector {
    return (canvas, onMessage) => connect(canvas, (message) => {
        reportGameFailure(message);
        if (message.type === 'ui-state') {
            runMetrics.observe(message.patch);
        }
        onMessage(message);
    });
}

/** Reports what the game shows the player as a failure: a scene that broke, or "Something Went Wrong" (#212). */
function reportGameFailure(message: GameConnectionMessage): void {
    if (message.type === 'scene-error') {
        reportClientCrash({ kind: 'scene', message: message.message });
    } else if (message.type === 'ui-state' && typeof message.patch.fatalError === 'string') {
        reportClientCrash({ kind: 'fatal', message: message.patch.fatalError });
    }
}

/**
 * Returns the session for `crucibleId`, creating it (and ending any other)
 * when there is none. The game and its canvas are constructed exactly once
 * per `crucibleId` and are not tied to any React component's
 * mount/unmount - only `endGameSession()` tears them down. The canvas is
 * created detached; callers place it in the DOM via `mountGameCanvas()`.
 */
function ensureSession(crucibleId: string): GameSessionHandle {
    if (session && session.crucibleId === crucibleId) {
        return session;
    }

    if (session) {
        void endGameSession();
    }

    const canvas = createCanvas();
    const bridge = new GameStateBridge();
    const runMetrics = new ClientRunMetrics({
        crucibleId,
        record: recordClientMetricEvent,
        getDisplayInfo: getClientDisplayInfo,
    });
    const host = createGamePageHost(canvas, bridge, withGameMetrics(connectGameWorker, runMetrics), (message) => {
        for (const listener of sceneMessageListeners) {
            listener(message);
        }
    });

    session = {
        crucibleId,
        canvas,
        host,
        bridge,
        gameplayApi: host.gameplayApi,
    };
    unregisterCleanRenderSource = registerCleanRenderSource({
        isShown: () => canvas.isConnected,
        capture: (scale: number): Promise<CleanRenderResult> => host.captureCleanRender(scale),
        isPrimary: true,
    });
    notify();
    return session;
}

/**
 * The crucible page's session: the game worker starts at once and draws
 * the gathering, so the engine and the party's models are loading while
 * the party gathers. `ensureGameSession` later starts the run on the same
 * scene. A solo direct-entry run passes `withGathering: false`: nobody
 * gathers, so the scene only loads the run.
 */
export function ensureCrucibleSession(crucibleId: string, withGathering: boolean): GameSessionHandle {
    const handle = ensureSession(crucibleId);
    if (!handle.host.isRunStarted) {
        handle.host.beginGathering(withGathering);
    }
    return handle;
}

/** Sends a gathering scene command to the crucible session, if there is one. */
export function sendGatheringCommand(command: CrucibleGatheringCommand): void {
    session?.host.sendGathering(command);
}

/**
 * Starts the run for `crucibleId`: on the gathering's scene when the
 * crucible page started one, else on a new session (a reload of the
 * crucible page, rejoining a run already under way). No-op once the run has started.
 */
export function ensureGameSession(
    crucibleId: string,
    sessionConnection: CrucibleSessionConnection,
    sessionToken: string,
): GameSessionHandle {
    const handle = ensureSession(crucibleId);
    handle.host.startRun({ sessionConnection, sessionToken });
    notify();
    return handle;
}

/** Calls `listener` with what the crucible scene reports before a run takes it over (errors, preloads done). Returns the unsubscribe. */
export function subscribeSceneMessages(listener: (message: GameSceneMessage) => void): () => void {
    sceneMessageListeners.add(listener);
    return (): void => {
        sceneMessageListeners.delete(listener);
    };
}

export function getGameSession(): GameSessionHandle | undefined {
    return session;
}

/**
 * Tears down the running session's game and removes its canvas from the DOM.
 * The only place a game is shut down. Resolves once the game has stopped and
 * its socket has left, so a caller about to load another page can wait for
 * that rather than cutting the connection off mid-leave.
 */
export function endGameSession(): Promise<void> {
    if (!session) {
        return Promise.resolve();
    }

    const { canvas, host } = session;
    const stopped = host.dispose();
    canvas.remove();
    unregisterCleanRenderSource();
    session = undefined;
    notify();
    return stopped;
}

export function subscribeGameSession(listener: () => void): () => void {
    listeners.add(listener);
    return (): void => {
        listeners.delete(listener);
    };
}

function resolveTarget(target: Element | string): Element {
    if (typeof target !== 'string') {
        return target;
    }

    const resolved = document.querySelector(target);
    if (resolved === null) {
        throw new Error(`mountGameCanvas: no element matches selector "${target}"`);
    }
    return resolved;
}

/** Appends the running session's canvas into `target` (an element or a CSS selector). No-op if already mounted there. */
export function mountGameCanvas(target: Element | string): void {
    if (!session) {
        throw new Error('mountGameCanvas: no active game session');
    }

    const parent = resolveTarget(target);
    if (session.canvas.parentElement === parent) {
        return;
    }

    parent.appendChild(session.canvas);
    // The canvas is constructed fully detached (see createCanvas() above),
    // so the page host could never measure its real on-screen size - 
    // this is the first moment `getBoundingClientRect()` returns something
    // meaningful. Without this, the canvas (and the render worker's camera)
    // stay locked to the browser's default 300x150 canvas size for the
    // entire session. See `GamePageHost.syncCanvasSize`.
    session.host.syncCanvasSize();
    // Gameplay keys only count while the canvas has focus (see PageInput).
    session.host.focus();
}

/** Detaches the running session's canvas from the DOM without destroying it or ending the session. */
export function unmountGameCanvasFrom(target?: Element | string): void {
    if (!session) {
        return;
    }

    const { canvas } = session;
    if (canvas.parentElement === null) {
        return;
    }

    if (target !== undefined && resolveTarget(target) !== canvas.parentElement) {
        return;
    }

    canvas.remove();
}

/** Reactive read of the current session handle (or undefined), following this codebase's module-singleton-store hook convention (see `usePartyRealtimeSelector`). */
export function useGameSession(): GameSessionHandle | undefined {
    return useSyncExternalStore(subscribeGameSession, getGameSession);
}
